import { INSIGHTS_REACT_URL } from '@/environment'
import {
  getAccessToken,
  getCompanyNameKey,
  getSubdomain,
} from '@/service/activeSession'
import { accessTokenToTransferKey } from '@attensi/session'

const HIDE_LINK_TO_NEW_INSIGHTS_FLAG = 'hide_link_to_new_insights'

// `source=manager` makes Insights show its "Bring me back to Power BI" button,
// pointing back to Manager (ENGMAN-387 / INSI-2261).
const INSIGHTS_COMPLETION_FROM_MANAGER_PATH = '/completion?source=manager'

export type CompanyFeatureFlags = { feature_flags?: string[] }

/** Whether the company stays on the legacy PowerBI dashboard instead of Insights. */
export const keepsLegacyDashboard = (
  company: CompanyFeatureFlags | null | undefined
): boolean =>
  company?.feature_flags?.includes(HIDE_LINK_TO_NEW_INSIGHTS_FLAG) ?? false

/**
 * Build the authenticated entry URL into the Insights app.
 *
 * Insights authenticates via its own `/auth/callback-client` route: it takes a
 * one-time `transfer_key` minted from the current Manager session, the company
 * context, and a `return_url` to land on after auth. This is deliberately not
 * `@attensi/redirect`/`redirectAuthenticated` — those do not target that
 * callback path with a `return_url`.
 *
 * The `return_url` is encoded so its own `?source=manager` is not split into
 * the callback's params; Insights decodes it when reading the param.
 */
export const buildInsightsCallbackUrl = async (): Promise<string> => {
  const accessToken = await getAccessToken()
  const transferKey = accessToken
    ? await accessTokenToTransferKey(accessToken)
    : undefined
  const subdomain = await getSubdomain()
  const companyNameKey = await getCompanyNameKey()

  const url = `${INSIGHTS_REACT_URL}/auth/callback-client?transfer_key=${transferKey}&subdomain=${subdomain}&company_name_key=${companyNameKey}&return_url=${encodeURIComponent(INSIGHTS_COMPLETION_FROM_MANAGER_PATH)}`

  // Every caller navigates straight after building, so this logs the hand-off.
  // biome-ignore lint/suspicious/noConsole: deliberate trace of the Insights hand-off URL
  console.log('Navigating to Insights:', url)
  return url
}

/**
 * Open Insights in a new tab, or in this one where the browser refuses a tab.
 * Call it straight from the click handler, before any `await`.
 */
export const openInsightsInNewTab = async (): Promise<void> => {
  // iOS silently blocks a tab opened after an await, so it is opened blank
  // inside the tap and pointed at Insights once the URL is built (FAME-930).
  // The `noopener` feature would make this return null, so the opener is cut by hand.
  const tab = window.open('', '_blank')
  if (!tab) {
    window.location.assign(await buildInsightsCallbackUrl())
    return
  }
  tab.opener = null

  try {
    tab.location.href = await buildInsightsCallbackUrl()
  } catch (error: unknown) {
    tab.close()
    throw error
  }
}
